import { ArchiveBoxIcon, PencilIcon } from '@heroicons/react/16/solid';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/catalyst/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  FirmTaskTemplateSchema,
  FirmTaskTemplateStatus,
  useUpdateFirmTaskTemplateMutation,
} from '@/services/api/firmTaskTemplatesService';
import { getApiErrorMessage } from '@/utils/api-error';

interface TaskTemplateCardProps {
  firmId: string;
  template: FirmTaskTemplateSchema;
  onEdit: (template: FirmTaskTemplateSchema) => void;
}

const STATUS_CONFIG: Record<
  FirmTaskTemplateStatus,
  { label: string; badgeColor: 'lime' | 'zinc' | 'amber' }
> = {
  auto_create: { label: 'Auto', badgeColor: 'lime' },
  manual_only: { label: 'Manual', badgeColor: 'zinc' },
  archived: { label: 'Archived', badgeColor: 'amber' },
};

export const TaskTemplateCard = ({
  firmId,
  template,
  onEdit,
}: TaskTemplateCardProps) => {
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);

  const [updateTemplate, { isLoading: isUpdating }] =
    useUpdateFirmTaskTemplateMutation();

  const handleStatusChange = async (newStatus: FirmTaskTemplateStatus) => {
    if (newStatus === template.status) return;

    try {
      await updateTemplate({
        firmId,
        templateId: template.id,
        data: { status: newStatus },
      }).unwrap();

      const messages: Record<FirmTaskTemplateStatus, string> = {
        auto_create: 'Template will auto-create on new cases',
        manual_only: 'Template set to manual assignment only',
        archived: 'Template archived',
      };
      toast.success(messages[newStatus]);
    } catch (error) {
      toast.error(
        getApiErrorMessage(error, 'Failed to update firm task template'),
      );
    }
  };

  const handleArchive = async () => {
    setShowArchiveConfirm(false);
    await handleStatusChange('archived');
  };

  const handleUnarchive = async () => {
    // Unarchive defaults to auto_create status
    await handleStatusChange('auto_create');
  };

  // Format values for display
  const formatValue = (value: string) =>
    value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  const isArchived = template.status === 'archived';
  const statusConfig = STATUS_CONFIG[template.status];

  return (
    <>
      <div className={`px-3 py-2 ${isArchived ? 'opacity-60' : ''}`}>
        {/* Header row: Name + Actions */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 flex-1 min-w-0">
            <span className="text-xs font-medium text-zinc-950 dark:text-white truncate">
              {template.name}
            </span>
            <Badge
              color={statusConfig.badgeColor}
              className="text-[10px] py-0 px-1.5"
            >
              {statusConfig.label}
            </Badge>
          </div>

          <div className="flex items-center gap-1.5 flex-shrink-0">
            {/* Status dropdown (only for non-archived) */}
            {!isArchived && (
              <Select
                value={template.status}
                onValueChange={(value) =>
                  handleStatusChange(value as FirmTaskTemplateStatus)
                }
                disabled={isUpdating}
              >
                <SelectTrigger className="h-6 w-[110px] text-[11px] px-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto_create" className="text-xs">
                    Auto-create
                  </SelectItem>
                  <SelectItem value="manual_only" className="text-xs">
                    Manual only
                  </SelectItem>
                </SelectContent>
              </Select>
            )}

            {/* Unarchive button for archived templates */}
            {isArchived && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleUnarchive}
                disabled={isUpdating}
                className="h-6 text-[11px] px-2"
              >
                Unarchive
              </Button>
            )}

            <button
              type="button"
              onClick={() => onEdit(template)}
              className="p-1 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors"
              aria-label="Edit task template"
            >
              <PencilIcon className="h-3.5 w-3.5" />
            </button>

            {/* Archive button (only for non-archived) */}
            {!isArchived && (
              <button
                type="button"
                onClick={() => setShowArchiveConfirm(true)}
                className="p-1 text-zinc-400 hover:text-amber-600 dark:hover:text-amber-400 transition-colors"
                aria-label="Archive task template"
              >
                <ArchiveBoxIcon className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Description */}
        {template.description && (
          <p className="text-[11px] leading-tight text-zinc-500 dark:text-zinc-400 line-clamp-2 mt-0.5">
            {template.description}
          </p>
        )}

        {/* Metadata row */}
        <p className="text-[11px] text-zinc-400 dark:text-zinc-500 mt-0.5">
          <span className="font-medium">Type:</span>{' '}
          {formatValue(template.task_type)}
          {template.assignee_role && (
            <>
              {' · '}
              <span className="font-medium">Assignee:</span>{' '}
              {formatValue(template.assignee_role)}
            </>
          )}
          {template.sla_resolution_policy && (
            <>
              {' · '}
              <span className="font-medium">SLA:</span>{' '}
              {formatValue(template.sla_resolution_policy)}
            </>
          )}
          {template.created_by_name && (
            <>
              {' · '}
              <span className="font-medium">Created by:</span>{' '}
              {template.created_by_name}
            </>
          )}
        </p>
      </div>

      {/* Archive Confirmation Dialog */}
      <Dialog open={showArchiveConfirm} onOpenChange={setShowArchiveConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Archive Task Template</DialogTitle>
            <DialogDescription>
              Are you sure you want to archive &quot;{template.name}&quot;?
              Archived templates are hidden from lists but preserved for
              existing task references. You can unarchive it later.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setShowArchiveConfirm(false)}
              disabled={isUpdating}
            >
              Cancel
            </Button>
            <Button onClick={handleArchive} disabled={isUpdating}>
              {isUpdating ? 'Archiving...' : 'Archive'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
